/**
 * Minimal structural view of a LiveKit RemoteTrackPublication — only the members
 * this helper needs, so it stays pure and unit-testable without the SDK.
 */
export interface SubscribablePublication {
  isSubscribed?: boolean;
  setSubscribed: (subscribed: boolean) => void;
}

/**
 * Subscribe a newly-published remote track when the authoritative media decision
 * (proximity + virtual-room isolation + password) says we should receive this
 * participant's media.
 *
 * Why this exists: the client runs with `autoSubscribe: false`, and the debounced
 * subscription loop keys on a coarse per-participant `subscribed` boolean. Once a
 * participant is marked subscribed (e.g. mic-only at first subscribe), a camera
 * track published *afterwards* — a join race, or the camera toggled on later — is
 * never re-subscribed, so some viewers see a black tile until a rejoin/toggle
 * (SC-246, Issues 1 & 3). Calling this from a `RoomEvent.TrackPublished` handler
 * closes that gap.
 *
 * Idempotent (no-op if already subscribed) and null-safe. `shouldSubscribe` is the
 * caller's authoritative decision (from `resolveMediaSubscriptionForParticipant`).
 *
 * @returns true iff it issued `setSubscribed(true)` — callers use this to apply
 *          per-tier camera quality only when a subscribe actually happened.
 */
export function subscribeNewlyPublishedTrack(
  publication: SubscribablePublication | null | undefined,
  shouldSubscribe: boolean,
): boolean {
  if (!publication || !shouldSubscribe) return false;
  if (publication.isSubscribed) return false;
  publication.setSubscribed(true);
  return true;
}
